/**
 * The results bundle: the contract between the trainer and the page (spec §3.3). The upload route stores only a
 * bundle this parses, and the page parses a stored one again before drawing it. Every issue reads "<path>: <reason>".
 * Pure. api/_lib imports this module: relative imports only, never '@/'.
 *
 * @module services/budget/bundleSchema
 */
import { z } from 'zod';
import { addDays, rangeDays } from '../looker/lookerDateRange';
import { AD_PLATFORMS, BUNDLE_SCHEMA, MAX_SHIFT, OUTCOME_METRICS } from './budgetContract';

const MIN_DRAWS = 50;
/** |Σ allocation − budget| and |budget − Σ current_weekly| may each be this share of the budget. */
const BUDGET_TOLERANCE = 0.01;
/**
 * Every number within ±LIMIT, grid steps ≥ MIN_STEP: with simulator's MIN_MOVE, Task 9's (g) holds for every accepted
 * bundle, and so do boxes 2–4's sums of weekly and summary figures across channels (T13a).
 */
const LIMIT = 1e15;
const MIN_STEP = 1e-6;

type Report = (path: PropertyKey[], message: string) => void;

const sha256 = z.string().regex(/^[0-9a-f]{64}$/, 'Not a lowercase hex sha256');
// Postgres takes any 8-4-4-4-12 hex; z.uuid() also wants the RFC variant bits, which some stored ids lack.
const id = z.guid();
const day = z.iso.date();
const int = z.number().int().min(-LIMIT).max(LIMIT);
const money = z.number().nonnegative().max(LIMIT);
const bounded = z.number().min(-LIMIT).max(LIMIT);
const perChannel = z.record(z.string(), bounded);

export const RangedV1 = z
  .strictObject({ mean: bounded, lo: bounded, hi: bounded })
  .refine((r) => r.lo <= r.mean && r.mean <= r.hi, 'lo ≤ mean ≤ hi does not hold');
export type Ranged = z.infer<typeof RangedV1>;

/** Weekly spend from 0, rising; the mean and every draw hold one value per grid point. */
const CurveV1 = z
  .strictObject({ grid: z.array(bounded), mean: z.array(bounded), draws: z.array(z.array(bounded)).min(MIN_DRAWS) })
  .superRefine((c, ctx) => {
    const issue: Report = (path, message) => ctx.addIssue({ code: 'custom', path, message });
    if (c.grid[0] !== 0) issue(['grid', 0], 'The grid starts at 0');
    c.grid.forEach((x, i) => {
      if (i > 0 && !(x - c.grid[i - 1] >= MIN_STEP)) issue(['grid', i], `The grid rises by at least ${MIN_STEP} a step`);
    });
    if (c.mean.length !== c.grid.length) issue(['mean'], `${c.mean.length} values for ${c.grid.length} grid points`);
    c.draws.forEach((row, d) => {
      if (row.length !== c.grid.length) issue(['draws', d], `${row.length} values for ${c.grid.length} grid points`);
    });
  });

/** The trainer's fixture check (plan R24). The upload gate filters stored versions on passed, so it is the AND. */
const ValidationV1 = z
  .strictObject({
    fixture: z.string(),
    passed: z.boolean(),
    criteria: z.array(z.strictObject({ name: z.string(), passed: z.boolean(), detail: z.string() })).min(1),
  })
  .superRefine((v, ctx) => {
    const issue: Report = (path, message) => ctx.addIssue({ code: 'custom', path, message });
    const names = v.criteria.map((c) => c.name);
    names.forEach((n, i) => { if (names.indexOf(n) < i) issue(['criteria', i, 'name'], `${n} is listed twice`); });
    const all = v.criteria.every((c) => c.passed);
    if (v.criteria.length > 0 && v.passed !== all) issue(['passed'], all ? 'Every criterion passed' : 'A criterion failed');
  });

const BundleShape = z.strictObject({
  schema_version: z.literal(BUNDLE_SCHEMA),
  dataset: z.strictObject({ id, sha256 }),
  workspace_id: id,
  outcome_metric: z.enum(OUTCOME_METRICS),
  currency: z.string(),
  scope_account_ids: z.array(id),
  window: z.strictObject({ start: day, end: day, weeks: int.positive() }),
  channels: z.array(z.strictObject({ id: z.enum(AD_PLATFORMS), stopped: z.boolean(), last_active_week: day })),
  trainer: z.strictObject({
    version: z.string(),
    source_sha256: sha256,
    meridian: z.string(),
    trained_at: z.iso.datetime({ offset: true }),
    sampler: z.strictObject({ chains: int, adapt: int, burnin: int, keep: int, seed: int }),
    model: z.strictObject({ weeks_per_knot: int, knots: int }),
  }),
  controls: z.array(z.strictObject({ name: z.string(), weeks: z.array(day) })),
  diagnostics: z.strictObject({ max_rhat: bounded, divergences: int, r2: bounded, mape: bounded, held_out_weeks: int }),
  /** share is of the total outcome, media_share of the media total. */
  summary: z.record(z.string(), z.strictObject({
    spend: money, contribution: RangedV1, share: RangedV1, media_share: RangedV1, roi: RangedV1,
  })),
  baseline: z.strictObject({ contribution: RangedV1, share: RangedV1 }),
  weekly: z.array(z.strictObject({
    week: day, actual: bounded.nullable(), predicted: bounded, baseline: bounded, channels: perChannel,
  })),
  current_weekly: z.record(z.string(), money),
  curves: z.record(z.string(), CurveV1),
  /** Weekly, like current_weekly (P1): the trainer asks Meridian for Σ current × weeks, then divides by weeks. */
  optimizer: z.strictObject({ budget: money, allocation: z.record(z.string(), money) }),
  validation: ValidationV1.optional(),
});
type Shape = z.infer<typeof BundleShape>;

/** Channel ids are unique, and every per-channel map holds exactly them. */
const checkChannels = (b: Shape, issue: Report): void => {
  const ids: readonly string[] = b.channels.map((c) => c.id);
  ids.forEach((ch, i) => { if (ids.indexOf(ch) < i) issue(['channels', i, 'id'], `${ch} is listed twice`); });
  const maps: [PropertyKey[], object][] = [
    [['summary'], b.summary],
    [['current_weekly'], b.current_weekly],
    [['curves'], b.curves],
    [['optimizer', 'allocation'], b.optimizer.allocation],
    ...b.weekly.map((w, i): [PropertyKey[], object] => [['weekly', i, 'channels'], w.channels]),
  ];
  for (const [path, map] of maps) {
    const keys = Object.keys(map);
    keys.filter((k) => !ids.includes(k)).forEach((k) => issue([...path, k], 'Not one of the bundle’s channels'));
    new Set(ids).forEach((ch) => { if (!keys.includes(ch)) issue(path, `${ch} is missing`); });
  }
};

/** Every channel has as many draws as the first, so a total per draw is valid (spec §3.3). */
const checkDrawCounts = (b: Shape, issue: Report): void => {
  const [first, ...rest] = Object.entries(b.curves);
  if (!first) return;
  const [firstChannel, { draws }] = first;
  for (const [ch, c] of rest) {
    if (c.draws.length !== draws.length) issue(['curves', ch, 'draws'], `${c.draws.length} draws where ${firstChannel} has ${draws.length}`);
  }
};

const isMonday = (d: string): boolean => new Date(`${d}T00:00:00Z`).getUTCDay() === 1;

/** weekly holds window.weeks consecutive Mondays from window.start to window.end (plan R1). */
const checkWeeks = (b: Shape, issue: Report): void => {
  const { start, end, weeks } = b.window;
  // A failed date check is continuable, so this still runs: zod has reported start, and addDays would throw on it.
  if (!day.safeParse(start).success) return;
  if (!isMonday(start)) issue(['window', 'start'], `${start} is not a Monday`);
  // Day arithmetic, not addDays: a huge weeks would make an invalid Date, and toISOString throws.
  if (rangeDays({ start, end }) !== 7 * (weeks - 1) + 1) issue(['window', 'end'], `${weeks} weeks from ${start} do not end on ${end}`);
  if (b.weekly.length !== weeks) issue(['weekly'], `${b.weekly.length} rows for ${weeks} weeks`);
  b.weekly.forEach((w, i) => {
    const want = addDays(start, 7 * i);
    if (w.week !== want) issue(['weekly', i, 'week'], `Expected ${want}`);
  });
};

const total = (m: Record<string, number>): number => Object.values(m).reduce((s, v) => s + v, 0);

/** A stopped channel gets nothing; the allocation spends the budget, which is the current weekly total. */
const checkOptimizer = (b: Shape, issue: Report): void => {
  const { budget, allocation } = b.optimizer;
  const slack = BUDGET_TOLERANCE * budget;
  b.channels.filter((c) => c.stopped).forEach((c) => {
    if (b.current_weekly[c.id] !== 0) issue(['current_weekly', c.id], 'Must be 0 for a stopped channel');
    if (allocation[c.id] !== 0) issue(['optimizer', 'allocation', c.id], 'Must be 0 for a stopped channel');
  });
  if (Math.abs(total(allocation) - budget) > slack) issue(['optimizer', 'allocation'], `Sums to ${total(allocation)}, more than 1% from the budget`);
  if (Math.abs(budget - total(b.current_weekly)) > slack) {
    issue(['optimizer', 'budget'], `More than 1% from the current weekly total ${total(b.current_weekly)}`);
  }
};

/**
 * T13b: auto-allocate may give a running channel (1 + MAX_SHIFT) × its current, so its curve reaches that far, and box
 * 5's ● and the slider's current sit on it. R12's G = 2 × max(average, current) always does.
 */
const checkGridTops = (b: Shape, issue: Report): void => {
  const most = 1 + MAX_SHIFT;
  b.channels.filter((c) => !c.stopped).forEach((c) => {
    const grid = b.curves[c.id]?.grid ?? [];
    const top: number | undefined = grid[grid.length - 1];
    const current: number | undefined = b.current_weekly[c.id];
    // A missing curve or current, or an empty grid, is checkChannels' or CurveV1's issue.
    if (top === undefined || current === undefined) return;
    if (top < most * current) issue(['curves', c.id, 'grid'], `Tops out at ${top}, under ${most} × the current weekly ${current}`);
  });
};

export const BundleV1 = BundleShape.superRefine((b, ctx) => {
  const issue: Report = (path, message) => ctx.addIssue({ code: 'custom', path, message });
  checkChannels(b, issue);
  checkDrawCounts(b, issue);
  checkWeeks(b, issue);
  checkOptimizer(b, issue);
  checkGridTops(b, issue);
});
export type Bundle = z.infer<typeof BundleV1>;

const pathOf = (path: readonly PropertyKey[]): string => (path.length > 0 ? path.map(String).join('.') : '(root)');

export const parseBundle = (input: unknown): { ok: true; bundle: Bundle } | { ok: false; issues: string[] } => {
  const r = BundleV1.safeParse(input);
  if (r.success) return { ok: true, bundle: r.data };
  // An unknown key is named in the path, one issue per key.
  const issues = r.error.issues.flatMap((i) => (i.code === 'unrecognized_keys'
    ? i.keys.map((k) => `${pathOf([...i.path, k])}: Unrecognized key`)
    : [`${pathOf(i.path)}: ${i.message}`]));
  return { ok: false, issues };
};
